<script lang="ts">
    import DeleteTab from "@components/popup/templates/DeleteTab.svelte"
    import EditTab from "@components/popup/templates/EditTab.svelte"
    import ContextMenu from "@components/utilities/context-menu/ContextMenu.svelte"
    import ContextMenuLink from "@components/utilities/context-menu/ContextMenuLink.svelte"
    import { selectAndGotoTab, type TabClass } from "@store/pageTracker"
    import { createEventDispatcher, onDestroy } from "svelte"
    import TabMenuButton from "./TabMenuButton.svelte"

    export let tab: TabClass

    const dispatch = createEventDispatcher<{ select: void }>()

    let showing = false
    let showingDeletePrompt = false
    let showingRenamePrompt = false
    let dialogTimer: ReturnType<typeof setTimeout> | null = null

    $: id = "reading_tab_context_" + tab.id

    function debug(message: string, details?: Record<string, unknown>) {
        if (!import.meta.env.DEV) return
        console.info("[tab-menu]", message, { tab: tab.id, tabName: tab.name, ...details })
    }

    function openTab() {
        debug("open tab selected")
        selectAndGotoTab(tab.id)
        dispatch("select")
        showing = false
    }

    function afterMenuCloses(action: "rename" | "delete", callback: () => void) {
        debug("dialog requested", { action })
        showing = false
        if (dialogTimer) clearTimeout(dialogTimer)
        dialogTimer = setTimeout(() => {
            debug("dialog opening", { action })
            callback()
            dialogTimer = null
        }, 0)
    }

    onDestroy(() => {
        if (dialogTimer) clearTimeout(dialogTimer)
    })
</script>

<TabMenuButton
    variant="reader-tabs"
    label={`Open menu for ${tab.displayName}`}
    controls={id}
    expanded={showing}
    action={() => {
        showing = !showing
        debug("trigger clicked", { showing })
    }}
>
    {#if showing}
        <ContextMenu on:exit={() => (showing = false)} {id} placement="fixed" variant="reader-tabs">
            <ContextMenuLink icon="read" cb={openTab}>Open</ContextMenuLink>
            <ContextMenuLink
                icon="pen"
                cb={() => {
                    afterMenuCloses("rename", () => (showingRenamePrompt = true))
                }}>Rename</ContextMenuLink
            >
            <ContextMenuLink
                icon="bin"
                tone="danger"
                cb={() => {
                    afterMenuCloses("delete", () => (showingDeletePrompt = true))
                }}
            >
                Delete
            </ContextMenuLink>
        </ContextMenu>
    {/if}
</TabMenuButton>

{#if showingDeletePrompt}
    <DeleteTab {tab} on:exit={() => (showingDeletePrompt = false)} />
{/if}

{#if showingRenamePrompt}
    <EditTab {tab} on:exit={() => (showingRenamePrompt = false)} />
{/if}
